/**
 * A date range: a preset menu and the two dates it resolves to, all on screen.
 *
 * The preset list is longer than the first pass because the presets are the
 * point: "last 7 days" and "this month" are what an operator actually asks
 * for, and every one of those they can pick is a custom range they don't have
 * to type two dates for.
 */
import { useId } from 'react';
import { FilterSlot } from './filter-bar';

const DAY_MS = 86_400_000;
const iso = (d: Date) => {
  // Local calendar date, not UTC. `toISOString()` on an IST machine rolls back
  // past midnight and silently shifts every range a day early.
  const tz_offset = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tz_offset).toISOString().slice(0, 10);
};

const today = () => iso(new Date());
const days_ago = (n: number) => iso(new Date(Date.now() - n * DAY_MS));

export type DateRange = { from: string; to: string };

type Preset = { value: string; label: string; range: () => DateRange };

const PRESETS: Preset[] = [
  { value: 'today', label: 'Today', range: () => ({ from: today(), to: today() }) },
  { value: 'yesterday', label: 'Yesterday', range: () => ({ from: days_ago(1), to: days_ago(1) }) },
  { value: '7', label: 'Last 7 days', range: () => ({ from: days_ago(6), to: today() }) },
  { value: '30', label: 'Last 30 days', range: () => ({ from: days_ago(29), to: today() }) },
  { value: '90', label: 'Last 90 days', range: () => ({ from: days_ago(89), to: today() }) },
  {
    value: 'mtd',
    label: 'This month',
    range: () => {
      const d = new Date();
      return { from: iso(new Date(d.getFullYear(), d.getMonth(), 1)), to: today() };
    },
  },
  {
    value: 'last-month',
    label: 'Last month',
    range: () => {
      const d = new Date();
      return {
        from: iso(new Date(d.getFullYear(), d.getMonth() - 1, 1)),
        to: iso(new Date(d.getFullYear(), d.getMonth(), 0)),
      };
    },
  },
];

/** Which preset, if any, the current from/to happens to equal. */
export function preset_of(from: string, to: string): string {
  if (!from && !to) return '';
  for (const p of PRESETS) {
    const r = p.range();
    if (r.from === from && r.to === to) return p.value;
  }
  return 'custom';
}

/** Human label for the active range, for the filter chip. */
export function describe_range(from: string, to: string): string {
  const key = preset_of(from, to);
  const preset = PRESETS.find((p) => p.value === key);
  if (preset) return preset.label;
  if (from && to) return `${from} → ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Until ${to}`;
  return 'Any time';
}

export function DateRangeFilter({
  from,
  to,
  on_change,
  label = 'Date',
}: {
  from: string;
  to: string;
  on_change: (next: DateRange) => void;
  label?: string;
}) {
  const id = useId();
  const current = preset_of(from, to);

  function choose(value: string) {
    if (value === '') return on_change({ from: '', to: '' });
    if (value === 'custom') {
      /*
       * Seed the boxes with something valid so the list does not sit unfiltered while
       * somebody is halfway through picking two dates.
       */
      if (!from && !to) on_change({ from: days_ago(29), to: today() });
      return;
    }
    const preset = PRESETS.find((p) => p.value === value);
    if (preset) on_change(preset.range());
  }

  return (
    <>
      <FilterSlot label={label} html_for={id}>
        <select
          id={id}
          data-testid="filter-date-range"
          className="console-select"
          value={current}
          onChange={(e) => choose(e.target.value)}
        >
          <option value="">Any time</option>
          {PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
          <option value="custom">Custom range…</option>
        </select>
      </FilterSlot>

      {/*
        Always on screen, not hidden behind "Custom range…".
        
        Every list used to carry two visible date boxes; folding them into the preset
        dropdown meant an admin who wanted a specific window had to know to pick a menu
        item called "Custom" first — and until they did, the screen showed no way to type
        a date at all. Picking a preset now fills these in, which also makes the preset
        legible: "Last 7 days" is a phrase, "01 Aug → 07 Aug" is the query.
      */}
      <>
          <FilterSlot label="From" html_for={`${id}-from`}>
            <input
              id={`${id}-from`}
              type="date"
              className="console-field w-36"
              value={from}
              max={to || today()}
              onChange={(e) => on_change({ from: e.target.value, to })}
              data-testid="filter-from"
            />
          </FilterSlot>
          <FilterSlot label="To" html_for={`${id}-to`}>
            <input
              id={`${id}-to`}
              type="date"
              className="console-field w-36"
              value={to}
              min={from || undefined}
              max={today()}
              onChange={(e) => on_change({ from, to: e.target.value })}
              data-testid="filter-to"
            />
          </FilterSlot>
      </>
    </>
  );
}
